import { useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Card, Notice } from './Shell';
import { Stamp } from './Stamp';
import { dateTime } from '../lib/format';
import type { VisitReport } from '../lib/types';

const MAX_BYTES = 12 * 1024 * 1024;
const MAX_PHOTOS = 12;
const ACCEPT = 'image/jpeg,image/png,image/heic,image/webp';

interface UploadTicket {
  updateId: string;
  fileKey: string;
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
}

interface Pending {
  key: string;
  /** An object URL for the local file, so the thumbnail appears immediately. */
  preview: string;
}

/**
 * One update, as the pet's family reads it.
 *
 * The photographs come first and the note is set in ordinary prose, because
 * this is the only screen in the product that exists to be felt rather than
 * checked. Everywhere else a card is a record; here it is a postcard. The
 * ticked-off facts (fed, medication, potty breaks) sit underneath in stamps,
 * where they are findable but do not lead.
 */
export function UpdateCard({
  report,
  timezone,
  texting,
}: {
  report: VisitReport;
  timezone?: string;
  /** Present only on the sitter's side; the client's portal shows no send button. */
  texting?: TextingProps;
}) {
  return (
    <Card className="overflow-hidden">
      {report.photos.length > 0 && <PhotoGrid photos={report.photos} />}

      <div className="p-5">
        <p className="whitespace-pre-wrap text-[1.05rem] leading-relaxed">{report.note}</p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {report.fed && <Stamp tone="ok">Fed</Stamp>}
          {report.medicationGiven && <Stamp tone="ok">Medication given</Stamp>}
          {report.pottyBreaks !== undefined && report.pottyBreaks > 0 && (
            <Stamp tone="quiet">
              {report.pottyBreaks} potty {report.pottyBreaks === 1 ? 'break' : 'breaks'}
            </Stamp>
          )}
          <span className="ml-auto font-mono text-xs text-[color:var(--color-ink-faint)]">
            {dateTime(report.occurredAt, timezone)}
          </span>
        </div>

        {texting && <TextButton report={report} {...texting} />}
      </div>
    </Card>
  );
}

export interface TextingProps {
  tenantId: string;
  bookingId: string;
  /** False when the business has no number, or the client has not agreed. */
  available: boolean;
  /** Why not, said in the sitter's terms rather than as an error code. */
  unavailableReason?: string;
}

/**
 * Text this update to the client.
 *
 * Filing and sending are separate on purpose: filing is the record, sending
 * costs money and cannot be taken back. So a half-written note never reaches
 * anybody, and a busy afternoon of photographs does not quietly run up a bill.
 *
 * The photo count is worth saying out loud. Carriers will not carry every
 * picture that the portal will happily hold, so the confirmation reports what
 * actually went into the message and what is waiting in the portal instead,
 * rather than letting a sitter believe all twelve arrived.
 */
function TextButton({
  report,
  tenantId,
  bookingId,
  available,
  unavailableReason,
}: { report: VisitReport } & TextingProps) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!available) {
    return (
      <p className="mt-4 border-t border-[color:var(--color-rule)] pt-4 text-sm text-[color:var(--color-ink-soft)]">
        {unavailableReason ?? 'Texting is not set up yet. They can read this in their portal.'}
      </p>
    );
  }

  const send = () => {
    setState('sending');
    setError(null);
    void api<{ photosAttached: number; photosInPortal: number }>(
      `/tenants/${tenantId}/bookings/${bookingId}/reports/${report.reportId}/send`,
      { method: 'POST' }
    )
      .then((sent) => {
        setState('sent');
        setResult(
          sent.photosInPortal > 0
            ? `Sent with ${sent.photosAttached} ${sent.photosAttached === 1 ? 'photo' : 'photos'}. ` +
                `${sent.photosInPortal} too large to text, waiting in their portal.`
            : sent.photosAttached > 0
              ? `Sent with ${sent.photosAttached} ${sent.photosAttached === 1 ? 'photo' : 'photos'}.`
              : 'Sent.'
        );
      })
      .catch((err) => {
        setState('idle');
        setError(err instanceof ApiError ? err.message : 'That could not be sent just now.');
      });
  };

  return (
    <div className="mt-4 border-t border-[color:var(--color-rule)] pt-4">
      {error && (
        <p className="mb-3 text-sm text-[color:var(--color-flag)]">{error}</p>
      )}
      {state === 'sent' ? (
        <p className="text-sm text-[color:var(--color-ink-soft)]">{result}</p>
      ) : (
        <button type="button" className="btn btn-secondary" disabled={state === 'sending'} onClick={send}>
          {state === 'sending' ? 'Sending' : 'Text this to them'}
        </button>
      )}
    </div>
  );
}

/**
 * A single photograph fills the card. Two or more become a grid, and the
 * fourth onwards stack behind a count rather than turning the card into a
 * contact sheet: the point is a glimpse, and the rest are one tap away in the
 * booking.
 */
function PhotoGrid({ photos }: { photos: string[] }) {
  const shown = photos.slice(0, 3);
  const extra = photos.length - shown.length;

  if (photos.length === 1) {
    return (
      <img
        src={photos[0]}
        alt=""
        loading="lazy"
        className="aspect-[4/3] w-full bg-[color:var(--color-sheet-edge)] object-cover"
      />
    );
  }

  return (
    <div className="grid grid-cols-3 gap-0.5 bg-[color:var(--color-rule)]">
      {shown.map((url, index) => (
        <div key={url} className="relative">
          <img
            src={url}
            alt=""
            loading="lazy"
            className="aspect-square w-full bg-[color:var(--color-sheet-edge)] object-cover"
          />
          {index === shown.length - 1 && extra > 0 && (
            <span className="absolute inset-0 flex items-center justify-center bg-[color:var(--color-ink)]/55 font-display text-xl font-extrabold text-white">
              +{extra}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The sitter's side: write the update and send it.
 *
 * She is doing this one-handed, standing in her kitchen, with a dog against
 * her leg. So the note is the only required field, the photographs upload the
 * moment they are picked rather than on submit, and the three tick boxes carry
 * a default of nothing rather than of false: an update that says nothing about
 * medication should not read as "medication was not given".
 */
export function UpdateComposer({
  tenantId,
  bookingId,
  onPosted,
}: {
  tenantId: string;
  bookingId: string;
  onPosted: () => void;
}) {
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<Pending[]>([]);
  const [updateId, setUpdateId] = useState<string | undefined>();
  const [fed, setFed] = useState(false);
  const [medicationGiven, setMedicationGiven] = useState(false);
  const [pottyBreaks, setPottyBreaks] = useState('');
  const [uploading, setUploading] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const describe = (err: unknown) =>
    err instanceof ApiError ? err.message : 'Something went wrong. Try again.';

  /**
   * The same two-step upload a pet document uses: ask for a signed PUT, then
   * send the file straight to S3. Every photograph after the first passes back
   * the updateId it was given, so one update's pictures share one folder.
   */
  const addFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    const chosen = Array.from(files).slice(0, room);
    if (chosen.length < files.length) {
      setError(`An update can carry ${MAX_PHOTOS} photos. The rest were not added.`);
    }

    for (const file of chosen) {
      if (file.size > MAX_BYTES) {
        setError(`${file.name} is over 12MB. Send a smaller version.`);
        continue;
      }
      setUploading((n) => n + 1);
      void (async () => {
        const preview = URL.createObjectURL(file);
        try {
          const ticket = await api<UploadTicket>(
            `/tenants/${tenantId}/bookings/${bookingId}/reports/upload-url`,
            {
              method: 'POST',
              body: {
                fileName: file.name,
                contentType: file.type,
                sizeBytes: file.size,
                updateId,
              },
            }
          );
          // Claim the folder for the rest of this update's photographs.
          setUpdateId((current) => current ?? ticket.updateId);

          const put = await fetch(ticket.uploadUrl, {
            method: 'PUT',
            headers: ticket.requiredHeaders,
            body: file,
          });
          if (!put.ok) throw new Error(`Upload failed with ${put.status}`);

          setPhotos((current) => [...current, { key: ticket.fileKey, preview }]);
        } catch (err) {
          URL.revokeObjectURL(preview);
          setError(describe(err));
        } finally {
          setUploading((n) => n - 1);
        }
      })();
    }

    // Let the same file be picked again after a failure.
    if (fileInput.current) fileInput.current.value = '';
  };

  const removePhoto = (key: string) => {
    setPhotos((current) => {
      const gone = current.find((p) => p.key === key);
      if (gone) URL.revokeObjectURL(gone.preview);
      return current.filter((p) => p.key !== key);
    });
  };

  const send = (event: React.FormEvent) => {
    event.preventDefault();
    if (!note.trim()) {
      setError('Write a line about how they are doing.');
      return;
    }
    setSending(true);
    setError(null);

    void (async () => {
      try {
        await api(`/tenants/${tenantId}/bookings/${bookingId}/reports`, {
          method: 'POST',
          body: {
            note: note.trim(),
            photoKeys: photos.map((p) => p.key),
            // Only sent when ticked. An absent field means "not mentioned",
            // which is not the same as "no".
            ...(fed ? { fed: true } : {}),
            ...(medicationGiven ? { medicationGiven: true } : {}),
            ...(pottyBreaks ? { pottyBreaks: Number(pottyBreaks) } : {}),
          },
        });

        for (const photo of photos) URL.revokeObjectURL(photo.preview);
        setNote('');
        setPhotos([]);
        setUpdateId(undefined);
        setFed(false);
        setMedicationGiven(false);
        setPottyBreaks('');
        onPosted();
      } catch (err) {
        setError(describe(err));
      } finally {
        setSending(false);
      }
    })();
  };

  const busy = sending || uploading > 0;

  return (
    <Card tint="pink" className="p-5">
      {error && (
        <div className="mb-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <form onSubmit={send}>
        <label className="field-label" htmlFor="update-note">
          How are they doing?
        </label>
        <textarea
          id="update-note"
          className="field-textarea"
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={4000}
          placeholder="Slept through the night and ate every bite of breakfast. We did two laps of the garden and she has claimed the sunny end of the sofa."
        />

        {(photos.length > 0 || uploading > 0) && (
          <ul className="mt-4 flex flex-wrap gap-2">
            {photos.map((photo) => (
              <li key={photo.key} className="relative">
                <img
                  src={photo.preview}
                  alt=""
                  className="h-20 w-20 rounded-[8px] border border-[color:var(--color-rule)] object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(photo.key)}
                  className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-[color:var(--color-rule)] bg-white text-sm leading-none text-[color:var(--color-ink-soft)] shadow-sm hover:text-[color:var(--color-flag)]"
                  aria-label="Remove this photo"
                >
                  &times;
                </button>
              </li>
            ))}
            {Array.from({ length: uploading }, (_, index) => (
              <li
                key={`uploading-${index}`}
                className="flex h-20 w-20 items-center justify-center rounded-[8px] border border-dashed border-[color:var(--color-rule)] bg-white font-mono text-[0.6rem] uppercase tracking-widest text-[color:var(--color-ink-faint)]"
              >
                Adding
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => addFiles(event.target.files)}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => fileInput.current?.click()}
            disabled={photos.length >= MAX_PHOTOS}
          >
            Add photos
          </button>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={fed} onChange={(e) => setFed(e.target.checked)} />
            Fed
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={medicationGiven}
              onChange={(e) => setMedicationGiven(e.target.checked)}
            />
            Medication given
          </label>
          <label className="flex items-center gap-2 text-sm">
            Potty breaks
            <input
              type="number"
              min={0}
              max={24}
              value={pottyBreaks}
              onChange={(e) => setPottyBreaks(e.target.value)}
              className="field-input w-16 px-2 py-1 text-center"
            />
          </label>
        </div>

        <div className="mt-5 flex items-center gap-4">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {sending ? 'Sending' : 'Send update'}
          </button>
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            {uploading > 0 ? 'Waiting for the photos to finish.' : 'They will see it straight away.'}
          </p>
        </div>
      </form>
    </Card>
  );
}
